/**
 * @file Mutations to modify data using React Query.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { QueryKeys } from '../constants';
import { AuthContext } from '../state/Context';
import { toast } from '../utils/toastUtil';
import { apiClient } from '../network';
import { authClient } from '../auth';

/**
 * Authorize.
 */
export const useTokenMutation = () => {
  const { setTokens } = useContext(AuthContext);

  return useMutation({
    mutationFn: async ({ data }) => {
      return await authClient.signinCallback(data.code);
    },
    onSuccess: (data) => {
      setTokens({
        accessToken: data.access_token,
        idToken: data.id_token,
        refreshToken: data.refresh_token,
      });
    },
  });
};

/**
 * Create scenario.
 */
export const useCloneScenarioMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scenarioId, data }) => {
      const response = await apiClient.post(
        `/scenarios/${scenarioId}/clone`,
        data,
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast(t('common.toast.saved'));
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SCENARIOS] });
      queryClient.setQueryData([QueryKeys.SCENARIOS, data.id], data);
    },
  });
};

/**
 * Update scenario.
 */
export const useUpdateScenarioMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scenarioId, data }) => {
      const response = await apiClient.put(`/scenarios/${scenarioId}`, data);
      return response.data;
    },
    onSuccess: (data) => {
      toast(t('common.toast.saved'));
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SCENARIOS] });
      queryClient.setQueryData([QueryKeys.SCENARIOS, data.id], data);
    },
  });
};

/**
 * Update scenario unit map.
 */
export const useUpdateScenarioUnitMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scenarioId, unitId, data }) => {
      const response = await apiClient.put(
        `/scenarios/${scenarioId}/units/${unitId}`,
        data,
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast(t('common.toast.saved'));
      // TODO: investigate performance drawbacks of invalidating list
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.SCENARIO_UNIT_MAP],
      });
      queryClient.setQueryData([QueryKeys.SCENARIO_UNIT_MAP, data.id], data);
    },
  });
};

/**
 * Update scenario patient category map.
 */
export const useUpdateScenarioPatientCategoryMutation = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scenarioId, patientCategoryId, data }) => {
      const response = await apiClient.put(
        `/scenarios/${scenarioId}/patient-categories/${patientCategoryId}`,
        data,
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast(t('common.toast.saved'));
      // TODO: investigate performance drawbacks of invalidating list
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.SCENARIO_PATIENT_CATEGORY_MAP],
      });
      queryClient.setQueryData(
        [QueryKeys.SCENARIO_PATIENT_CATEGORY_MAP, data.id],
        data,
      );
    },
  });
};

/**
 * Simulate.
 */
export const useScenarioSimulateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scenarioId }) => {
      const response = await apiClient.post(
        `/scenarios/${scenarioId}/simulate`,
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SIMULATIONS] });
      queryClient.setQueryData([QueryKeys.SIMULATIONS, data.id], data);
    },
  });
};

/**
 * Download simulation log.
 */
export const useSimulationLogDownloadMutation = () => {
  return useMutation({
    mutationFn: async ({ simulationId }) => {
      const response = await apiClient.get(
        `/simulation-logs/${simulationId}/download`,
      );
      return {
        filename: `simulation_${simulationId}.log`,
        content: response.data,
      };
    },
    onSuccess: (data) => {
      saveAs(new Blob([data.content], { type: 'text/plain' }), data.filename);
    },
  });
};
